import { useMemo } from 'react'
import PropTypes from 'prop-types'
import BacCardDesign from '../BacCardDesignList/BacCardDesign'
import { Button } from '@material-ui/core'
import isNull from 'lodash/isNull'
import isEmpty from 'lodash/isEmpty'
import { UploadPhoto } from '../../../CustomSVG/UploadPhoto'
import { ChooseDesign } from '../../../CustomSVG/ChooseDesign'
import makeStyles from '@material-ui/core/styles/makeStyles'

const useStyles = makeStyles((theme) => ({
    designsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        columnGap: '0.5rem',
        rowGap: '0.5rem',
        paddingLeft: 0,
        margin: 0,
        maxWidth: 350,
        justifyItems: 'center',
        [theme.breakpoints.up('sm')]: {
            gridTemplateColumns: 'repeat(3, 1fr)',
            columnGap: '1rem',
            maxWidth: '600px',
        },
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
            maxWidth: '0px',
        },
        [theme.breakpoints.up('lg')]: {
            gridTemplateColumns: 'repeat(3, 1fr)',
        },
    },
    item: {
        display: 'flex',
        flexShrink: '1',
        flexGrow: '1',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: '100%',
        [theme.breakpoints.down('xs')]: {
            '&.hide-on-extra-small-and-down': {
                display: 'none',
            },
        },
        [theme.breakpoints.up('sm')]: {
            maxWidth: '180px',

            '&.hide-on-medium-and-down': {
                display: 'none',
            },
        },
        [theme.breakpoints.up('md')]: {
            minWidth: '180px',
            minHeight: '118px',
        },
    },
    uploadOrChooseButtonContainer: {
        gridColumn: 'span 1',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        border: '2px solid #4F4F4F',
        borderRadius: '4px',
        width: '100%',
        height: '100%',
        padding: '1rem',
        cursor: 'pointer',
        [theme.breakpoints.down('xs')]: {
            height: '106px',
        },
        [theme.breakpoints.up(400)]: {
            height: '118px',
            width: '180px',
        },
    },
    uploadOrChooseButton: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
        textTransform: 'none',
        borderRadius: '8px',
        width: '100%',
        all: 'unset!important',
        cursor: 'pointer',
    },
    textOnButton: {
        paddingTop: '8px',
        fontSize: '16px',
        textAlign: 'center',
        fontWeight: '550',
        color: 'rgba(32, 32, 32, 0.70)',
    },
    textContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
}))
const BacCardDesignSelector = ({
    itemCollection = [],
    cloudinaryIdProperty,
    imageUrlProperty,
    designNameProperty,
    selectedItem,
    onItemSelected,
    handleImageChange,
    hasSelectedPhoto,
    setImageRulesCheckResult,
    imageInputRef,
    overlay,
    displayLimit = null,
    onShowAllItemsSelected,
    setModalIsOpen,
}) => {
    const shouldDisplayAllDesignsButton = () => {
        return !isNull(displayLimit) && itemCollection.length > displayLimit
    }

    const items = useMemo(() => {
        if (isNull(itemCollection)) {
            return []
        }

        if (isNull(displayLimit)) {
            return itemCollection
        }

        return itemCollection.slice(0, displayLimit)
    }, [itemCollection, displayLimit])

    const classes = useStyles()

    const getDisplayClasses = (index) => {
        if (index < 2) {
            return ''
        }
        return 'hide-on-extra-small-and-down'
    }
    return (
        <div
            className={classes.designsContainer}
            aria-label="Card design options"
            tabIndex={0}
        >
            {isEmpty(items) && <li>No designs available</li>}
            {items?.map((item, index) => (
                <li
                    key={item.id}
                    className={`${classes.item} ${getDisplayClasses(index)}`}
                    tabIndex={0}
                    onClick={() => onItemSelected && onItemSelected(item)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onItemSelected && onItemSelected(item)
                        }
                    }}
                >
                    <BacCardDesign
                        item={item}
                        cloudinaryId={item[cloudinaryIdProperty]}
                        imageUrl={item[imageUrlProperty]}
                        imageTitle={`${item[designNameProperty]}`}
                        imageAlt={`${item[designNameProperty]} Gift Design`}
                        selectedItem={selectedItem}
                        onItemSelected={onItemSelected}
                        overlay={overlay}
                    />
                </li>
            ))}

            {shouldDisplayAllDesignsButton() && (
                <>
                    <div
                        role="button"
                        tabIndex={0}
                        aria-label="Upload a photo"
                        onClick={() => {
                            setImageRulesCheckResult({})
                            setModalIsOpen(true)
                        }}
                        className={classes.uploadOrChooseButtonContainer}
                    >
                        <Button
                            role="button"
                            tabIndex={0}
                            className={classes.uploadOrChooseButton}
                            variant={
                                hasSelectedPhoto ? 'outlined' : 'contained'
                            }
                            aria-label="Upload a photo"
                            onClick={() => {
                                setImageRulesCheckResult({})
                                setModalIsOpen(true)
                            }}
                        >
                            <div className={classes.textContainer}>
                                <UploadPhoto />
                                <span className={classes.textOnButton}>
                                    Upload a <br /> photo
                                </span>
                            </div>
                        </Button>

                        <div style={{ display: 'none' }}>
                            <input
                                type="file"
                                accept="image/jpeg,image/png"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    handleImageChange(e, () =>
                                        setModalIsOpen(true)
                                    )
                                }}
                                required
                                ref={imageInputRef}
                                id="image-file-upload"
                            />
                        </div>
                    </div>
                    <div
                        role="button"
                        tabIndex={0}
                        aria-label="Choose design"
                        className={`${classes.uploadOrChooseButtonContainer} hide-on-medium-and-down`}
                        onClick={onShowAllItemsSelected}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={onShowAllItemsSelected}
                            className={classes.uploadOrChooseButton}
                        >
                            <div className={classes.textContainer}>
                                <ChooseDesign />
                                <span className={classes.textOnButton}>
                                    Choose <br />
                                    design
                                </span>
                            </div>
                        </Button>
                    </div>
                </>
            )}
        </div>
    )
}

BacCardDesignSelector.propTypes = {
    itemCollection: PropTypes.array,
    cloudinaryIdProperty: PropTypes.string.isRequired,
    imageUrlProperty: PropTypes.string.isRequired,
    designNameProperty: PropTypes.string.isRequired,
    displayLimit: PropTypes.number,
    selectedItem: PropTypes.object,
    onItemSelected: PropTypes.func,
    onShowAllItemsSelected: PropTypes.func,
    handleImageChange: PropTypes.func,
    hasSelectedPhoto: PropTypes.bool,
    setImageRulesCheckResult: PropTypes.func,
    setModalIsOpen: PropTypes.func.isRequired,
    overlay: PropTypes.element,
    imageInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any }),
    ]),
}

export default BacCardDesignSelector
